import type { FlockEntry } from "./DailyReportPage";
import type { FeedFormula } from "@/types/models";
import AlreadySaved, { type SavedDay } from "./AlreadySaved";

interface Props {
  flocks: FlockEntry[];
  formulas: FeedFormula[];
  onChange: (index: number, field: keyof FlockEntry, value: string) => void;
  // The day's feed batches already saved. A batch is a point-in-time event,
  // not a daily total, so it is never pre-filled — it is listed instead.
  saved: SavedDay;
}

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

const selectClass =
  "w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

export default function StepFeedPrepared({ flocks, formulas, onChange, saved }: Props) {
  return (
    <div>
      <h4 className="mb-1 text-base font-semibold text-gray-800 dark:text-white/90">
        Feed Prepared (Stock Out)
      </h4>
      <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
        How much feed was prepared for each flock today?
      </p>
      <AlreadySaved saved={saved} verb="prepared" adds="another batch" />
      <div className="space-y-4">
        {flocks.map((f, i) => (
          <div
            key={f.flock_id}
            className="rounded-xl border border-gray-200 p-4 dark:border-gray-700"
          >
            <div className="mb-3 flex items-center justify-between">
              <h5 className="text-sm font-semibold text-gray-800 dark:text-white/90">
                {f.shed_code || f.flock_type}
              </h5>
              <span className="text-xs text-gray-500 dark:text-gray-400">{f.batch_name}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                  Feed Prepared (kg)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={f.feed_prepared_kg}
                  onChange={(e) => onChange(i, "feed_prepared_kg", e.target.value)}
                  className={inputClass}
                  placeholder="e.g. 500"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                  Feed Formula{parseFloat(f.feed_prepared_kg) > 0 ? " *" : ""}
                </label>
                <select
                  value={f.formula_id}
                  onChange={(e) => onChange(i, "formula_id", e.target.value)}
                  className={selectClass}
                >
                  <option value="">Select formula</option>
                  {formulas.map((fm) => (
                    <option key={fm.id} value={fm.id}>
                      {fm.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
